import React, { createContext, useContext, useState, useEffect } from 'react';
import { GlassType } from '../types/glassTypes';
import { getClgGlassTypes, getOldCastleGlassTypes, fallbackClgGlassTypes, fallbackOldCastleGlassTypes, processUploadedCSV } from '../api/csvApi';
import { GLASS_TYPE_CATEGORIES, getAvailableGlassTypeGroups, loadGlassTypesByGroup } from '../utils/glassTypeLoader';

// Define available data sources
export type GlassDataSource = 'CLG' | 'OldCastle' | 'custom' | string;

// Define glass type group (subdirectory)
export type GlassTypeGroup = 'whole' | string;

interface GlassTypesContextType {
  glassTypes: GlassType[];
  customGlassTypes: GlassType[];
  enabledTypes: string[];
  allUniqueTypes: string[];
  toggleEnabledType: (type: string) => void;
  addCustomGlassType: (glassType: Omit<GlassType, 'id'>) => void;
  removeCustomGlassType: (id: string) => void;
  selectedSource: string;
  availableSources: string[];
  setSelectedSource: (source: string) => void;
  addNewSource: (sourceName: string) => void;
  clearGlassTypes: () => void;
  isLoading: boolean;
  uploadCSVFile: (file: File) => Promise<boolean>;
  removeUploadedSource: (sourceName: string) => void;
  isUserUploadedSource: (sourceName: string) => boolean;
  availableGroups: string[];
  selectedGroup: string;
  setSelectedGroup: (group: string) => void;
}

const GlassTypesContext = createContext<GlassTypesContextType | undefined>(undefined);

// Helper function to generate UUID that works in all environments
function generateUUID() {
  // Check if the crypto.randomUUID is available
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback implementation for browsers that don't support crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Local storage keys
const STORAGE_KEYS = {
  ENABLED_TYPES: 'enabledGlassTypes',
  SELECTED_SOURCE: 'selectedGlassSource',
  AVAILABLE_SOURCES: 'availableGlassSources',
  CUSTOM_GLASS_TYPES: 'customGlassTypes',
  USER_UPLOADED_SOURCES: 'userUploadedSources',
  USER_UPLOADED_GLASS_TYPES: 'userUploadedGlassTypes',
  SELECTED_GROUP: 'selectedGlassGroup',
  AVAILABLE_GROUPS: 'availableGlassGroups'
};

export const GlassTypesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for glass types from different sources
  const [glassTypes, setGlassTypes] = useState<GlassType[]>([]);

  // State for custom glass types
  const [customGlassTypes, setCustomGlassTypes] = useState<GlassType[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CUSTOM_GLASS_TYPES);
    return saved ? JSON.parse(saved) : [];
  });

  // State for enabled types
  const [enabledTypes, setEnabledTypes] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ENABLED_TYPES);
    return saved ? JSON.parse(saved) : ['mirror', 'laminated', 'monolithic', 'ig'];
  });

  // State for selected data source
  const [selectedSource, setSelectedSource] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_SOURCE);
    return saved || 'default';
  });

  // State for available data sources
  const [availableSources, setAvailableSources] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AVAILABLE_SOURCES);
    return saved ? JSON.parse(saved) : ['default', 'CLG', 'OldCastle'];
  });

  // State for user-uploaded sources
  const [userUploadedSources, setUserUploadedSources] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.USER_UPLOADED_SOURCES);
    return saved ? JSON.parse(saved) : [];
  });

  // State for user-uploaded glass types
  const [userUploadedGlassTypes, setUserUploadedGlassTypes] = useState<Record<string, GlassType[]>>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.USER_UPLOADED_GLASS_TYPES);
    return saved ? JSON.parse(saved) : {};
  });

  // State for available glass type groups (subdirectories)
  const [availableGroups, setAvailableGroups] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AVAILABLE_GROUPS);
    return saved ? JSON.parse(saved) : ['whole'];
  });

  // State for selected glass type group
  const [selectedGroup, setSelectedGroup] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SELECTED_GROUP);
    return saved || 'whole';
  });

  // State for loading status
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Calculate all unique types once when glassTypes or customGlassTypes change
  // Normalize types to ensure consistent casing
  const allUniqueTypes = React.useMemo(() => {
    const types = new Set<string>();

    // Normalize types to ensure consistent casing
    glassTypes.forEach(type => {
      const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
      types.add(normalizedType);
    });

    customGlassTypes.forEach(type => {
      const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
      types.add(normalizedType);
    });

    return Array.from(types);
  }, [glassTypes, customGlassTypes]);

  // Save enabled types to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ENABLED_TYPES, JSON.stringify(enabledTypes));
  }, [enabledTypes]);

  // Save selected source to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_SOURCE, selectedSource);
  }, [selectedSource]);

  // Save available sources to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.AVAILABLE_SOURCES, JSON.stringify(availableSources));
  }, [availableSources]);

  // Save user-uploaded sources to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.USER_UPLOADED_SOURCES, JSON.stringify(userUploadedSources));
  }, [userUploadedSources]);

  // Save user-uploaded glass types to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.USER_UPLOADED_GLASS_TYPES, JSON.stringify(userUploadedGlassTypes));
  }, [userUploadedGlassTypes]);

  // Save selected group to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SELECTED_GROUP, selectedGroup);
  }, [selectedGroup]);

  // Save available groups to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.AVAILABLE_GROUPS, JSON.stringify(availableGroups));
  }, [availableGroups]);

  // Load available glass type groups on component mount
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groups = await getAvailableGlassTypeGroups();
        if (groups.length > 0) {
          setAvailableGroups(groups);
          
          // If the currently selected group is not in the available groups,
          // select the first available group
          if (!groups.includes(selectedGroup)) {
            setSelectedGroup(groups[0]);
          }
        }
      } catch (error) {
        console.error('Error loading available glass type groups:', error);
      }
    };
    
    loadGroups();
  }, []);

  // Save custom glass types to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CUSTOM_GLASS_TYPES, JSON.stringify(customGlassTypes));
  }, [customGlassTypes]);

  // Update enabled types when new types are added, but only if they're not already enabled
  // This effect runs only once when the component mounts to avoid infinite loops
  useEffect(() => {
    // Get normalized versions of current enabled types for comparison
    const normalizedEnabledTypes = enabledTypes.map(type =>
      type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
    );

    // Find truly new types that aren't already enabled (case-insensitive)
    const newTypes = allUniqueTypes.filter(type => {
      const normalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return !normalizedEnabledTypes.includes(normalizedType);
    });

    if (newTypes.length > 0) {
      console.log('Adding new glass types to enabled types:', newTypes);
      setEnabledTypes(prev => [...prev, ...newTypes]);
    }
  }, []);  // Empty dependency array means this runs only once on mount

  // Update enabled types when new types are added, but only if they're not already enabled
  // This effect runs only once when the component mounts to avoid infinite loops
  useEffect(() => {
    // Get normalized versions of current enabled types for comparison
    const normalizedEnabledTypes = enabledTypes.map(type =>
      type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
    );

    // Find truly new types that aren't already enabled (case-insensitive)
    const newTypes = allUniqueTypes.filter(type => {
      const normalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return !normalizedEnabledTypes.includes(normalizedType);
    });

    if (newTypes.length > 0) {
      console.log('Adding new glass types to enabled types:', newTypes);
      setEnabledTypes(prev => [...prev, ...newTypes]);
    }
  }, []);  // Empty dependency array means this runs only once on mount

  // Load glass types from the selected source and group
  useEffect(() => {
    // Load glass types based on selected source and group
    const loadGlassTypes = async () => {
      let types: GlassType[] = [];
      setIsLoading(true);

      try {
        console.log(`Loading glass types for source: ${selectedSource}, group: ${selectedGroup}`);

        switch (selectedSource) {
          case 'CLG':
          case 'OldCastle':
            // Load from the selected group
            if (selectedGroup === 'whole') {
              // For the 'whole' group, use the existing API functions
              if (selectedSource === 'CLG') {
                types = await getClgGlassTypes();
              } else {
                types = await getOldCastleGlassTypes();
              }
            } else {
              // For other groups, use the new loadGlassTypesByGroup function
              types = await loadGlassTypesByGroup(selectedGroup, selectedSource);
            }
            break;
          default:
            // Check if it's a user-uploaded source
            if (userUploadedSources.includes(selectedSource)) {
              types = userUploadedGlassTypes[selectedSource] || [];
            } else {
              // For other sources or if no source is selected, return empty array
              types = [];
            }
            break;
        }

        console.log(`Loaded ${types.length} glass types for source ${selectedSource}`);
        if (types.length > 0) {
          console.log('First glass type:', types[0]);

          // Extract unique types for filtering with proper normalization
          const uniqueTypes = Array.from(
            new Set(
              types.map(type => {
                // Normalize the type for consistent casing
                return type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
              })
            )
          );
          console.log('Unique types found:', uniqueTypes);

          // Count types by category for debugging
          const typeCounts = {};
          types.forEach(type => {
            const normalizedType = type.type.charAt(0).toUpperCase() + type.type.slice(1).toLowerCase();
            typeCounts[normalizedType] = (typeCounts[normalizedType] || 0) + 1;
          });
          console.log('Glass types by category:', typeCounts);

          // Update enabled types with the normalized unique types
          if (uniqueTypes.length > 0) {
            // Only update if we have types to avoid clearing existing enabled types
            setEnabledTypes(uniqueTypes);

            // Find which standard categories are present in our data
            const presentCategories = GLASS_TYPE_CATEGORIES.filter(category =>
              uniqueTypes.some(type =>
                type.toLowerCase() === category.toLowerCase() ||
                type.toLowerCase().includes(category.toLowerCase()) ||
                category.toLowerCase().includes(type.toLowerCase())
              )
            );

            console.log('Present standard categories:', presentCategories);

            // Update enabled types to include all present standard categories
            if (presentCategories.length > 0) {
              setEnabledTypes(prev => {
                const normalizedPrev = prev.map(t => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
                const newTypes = presentCategories.filter(cat =>
                  !normalizedPrev.some(t =>
                    t === cat || t.includes(cat) || cat.includes(t)
                  )
                );

                if (newTypes.length > 0) {
                  console.log('Adding missing standard categories:', newTypes);
                  return [...prev, ...newTypes];
                }
                return prev;
              });
            }
          }
        }
      } catch (error) {
        console.error(`Error loading glass types for source ${selectedSource}:`, error);
        // Use fallback data if loading fails
        switch (selectedSource) {
          case 'CLG':
            types = fallbackClgGlassTypes;
            break;
          case 'OldCastle':
            types = fallbackOldCastleGlassTypes;
            break;
          default:
            types = [];
            break;
        }
      } finally {
        setIsLoading(false);
      }

      setGlassTypes(types);
    };

    // Only load if we have a valid source
    if (selectedSource) {
      loadGlassTypes();
    }
  }, [selectedSource, selectedGroup, userUploadedSources, userUploadedGlassTypes]);

  const toggleEnabledType = (type: string) => {
    // Normalize the type for consistent casing
    const normalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

    setEnabledTypes(prev => {
      // Normalize previous types for case-insensitive comparison
      const normalizedPrev = prev.map(t => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

      if (normalizedPrev.includes(normalizedType)) {
        // Remove the type (keep original casing in the array)
        return prev.filter(t =>
          t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() !== normalizedType
        );
      } else {
        // Add the normalized type
        return [...prev, normalizedType];
      }
    });

    console.log(`Toggled type: ${normalizedType}`);
  };

  const addCustomGlassType = (glassType: Omit<GlassType, 'id'>) => {
    const newGlassType: GlassType = {
      ...glassType,
      id: crypto.randomUUID(),
      source: 'custom'
    };
    setCustomGlassTypes(prev => [...prev, newGlassType]);
  };

  const removeCustomGlassType = (id: string) => {
    setCustomGlassTypes(prev => prev.filter(type => type.id !== id));
  };

  const clearGlassTypes = () => {
    setGlassTypes([]);
  };

  // Function to check if a source is user-uploaded
  const isUserUploadedSource = (sourceName: string): boolean => {
    return userUploadedSources.includes(sourceName);
  };

  // Function to upload a CSV file and add it as a new source
  const uploadCSVFile = async (file: File): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Extract source name from file name (remove extension)
      const sourceName = file.name.replace(/\.[^/.]+$/, "");
      
      // Process the uploaded CSV file
      const types = await processUploadedCSV(file, sourceName);
      
      if (types.length === 0) {
        console.error('No glass types found in uploaded CSV file');
        return false;
      }
      
      // Add the new source to available sources if it doesn't exist
      if (!availableSources.includes(sourceName)) {
        setAvailableSources(prev => [...prev, sourceName]);
      }
      
      // Add the source to user-uploaded sources if it doesn't exist
      if (!userUploadedSources.includes(sourceName)) {
        setUserUploadedSources(prev => [...prev, sourceName]);
      }
      
      // Store the glass types for this source
      setUserUploadedGlassTypes(prev => ({
        ...prev,
        [sourceName]: types
      }));
      
      // Switch to the new source
      setSelectedSource(sourceName);
      
      return true;
    } catch (error) {
      console.error('Error uploading CSV file:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to remove a user-uploaded source
  const removeUploadedSource = (sourceName: string): void => {
    // Only allow removing user-uploaded sources
    if (!userUploadedSources.includes(sourceName)) {
      console.error(`Cannot remove non-user-uploaded source: ${sourceName}`);
      return;
    }
    
    // Remove from user-uploaded sources
    setUserUploadedSources(prev => prev.filter(source => source !== sourceName));
    
    // Remove from available sources
    setAvailableSources(prev => prev.filter(source => source !== sourceName));
    
    // Remove the glass types for this source
    setUserUploadedGlassTypes(prev => {
      const newTypes = { ...prev };
      delete newTypes[sourceName];
      return newTypes;
    });
    
    // If the removed source was selected, switch to default
    if (selectedSource === sourceName) {
      setSelectedSource('default');
    }
  };

  const value = {
    glassTypes,
    customGlassTypes,
    enabledTypes,
    allUniqueTypes,
    toggleEnabledType,
    addCustomGlassType,
    removeCustomGlassType,
    selectedSource,
    availableSources,
    setSelectedSource,
    addNewSource: (sourceName: string) => {
      if (!availableSources.includes(sourceName)) {
        setAvailableSources(prev => [...prev, sourceName]);
      }
    },
    clearGlassTypes,
    isLoading,
    uploadCSVFile,
    removeUploadedSource,
    isUserUploadedSource,
    availableGroups,
    selectedGroup,
    setSelectedGroup
  };

  return (
    <GlassTypesContext.Provider value={value}>
      {children}
    </GlassTypesContext.Provider>
  );
};

export const useGlassTypes = () => {
  const context = useContext(GlassTypesContext);
  if (context === undefined) {
    throw new Error('useGlassTypes must be used within a GlassTypesProvider');
  }
  return context;
};
